/**
 * SignatureBlock
 *
 * Displays signing/completion information at the bottom of a note.
 * Shows primary signer credentials and, when available, co-signatories.
 *
 * - Uses signature_metadata from the note when available (denormalized credentials)
 * - Falls back to signerName + completionTimestamp when metadata is absent
 * - "Electronically Signed" for signed notes
 * - "Signed — Awaiting Co-signature" when cosign is pending
 * - "Completed" for notes completed without signing
 */

import React from 'react';
import { DownloadIcon } from '@icons/DownloadIcon';
import { CheckCircle as CheckCircleIcon, PendingActions as PendingIcon } from '@mui/icons-material';
import { alpha, Box, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import type { SignatureEntryResponse } from '@store/generated/notes';
import { formatDate } from '@utils/utils';

import { NoteSectionContainer } from './layouts/NoteSectionContainer';

export interface SignatureBlockProps {
  /** Whether the note was electronically signed (vs just completed) */
  isSigned: boolean;
  /** Fallback display name when signature_metadata is absent */
  signerName: string;
  /** Fallback completion timestamp (Unix seconds) when signature_metadata is absent */
  completionTimestamp?: number;
  /** Denormalized signing entries from the note — used when present */
  signatureMetadata?: SignatureEntryResponse[];
  /** Handler for downloading signed PDF (only shown when provided) */
  onDownloadPdf?: () => void;
  /** Whether the note is awaiting a supervisor co-signature */
  awaitingCosign?: boolean;
}

export const SignatureBlock: React.FC<SignatureBlockProps> = ({
  isSigned,
  signerName,
  completionTimestamp,
  signatureMetadata,
  onDownloadPdf,
  awaitingCosign = false,
}) => {
  const theme = useTheme();

  const accentColor = awaitingCosign
    ? theme.palette.warning.main
    : isSigned
      ? theme.palette.success.main
      : theme.palette.grey[500];

  // Derive entries from signature_metadata when available
  const hasMetadata = signatureMetadata && signatureMetadata.length > 0;

  // Latest author entry (most recent signed_at)
  const authorEntry = hasMetadata
    ? [...signatureMetadata]
        .filter(e => e.role === 'author')
        .sort((a, b) => new Date(b.signed_at).getTime() - new Date(a.signed_at).getTime())[0]
    : null;

  // All supervisor entries
  const supervisorEntries = hasMetadata
    ? signatureMetadata.filter(e => e.role === 'supervisor')
    : [];

  const hasCosignatories = supervisorEntries.length > 0;

  return (
    <NoteSectionContainer sx={{ mt: 2, mb: 4 }}>
      <Box
        sx={{
          border: 1,
          borderColor: alpha(accentColor, 0.3),
          borderRadius: 2,
          bgcolor: alpha(accentColor, 0.04),
          px: 3,
          py: 2.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {awaitingCosign
            ? <PendingIcon sx={{ fontSize: 18, color: accentColor }} />
            : <CheckCircleIcon sx={{ fontSize: 18, color: accentColor }} />
          }
          <Typography
            variant="overline"
            sx={{
              fontWeight: 600,
              letterSpacing: 1,
              color: accentColor,
              lineHeight: 1,
              flex: 1,
            }}
          >
            {awaitingCosign
              ? 'Signed — Awaiting Co-signature'
              : isSigned
                ? 'Electronically Signed'
                : 'Completed'}
          </Typography>
          {onDownloadPdf && (
            <Tooltip title="Download Signed Note" placement="left">
              <IconButton
                onClick={onDownloadPdf}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: accentColor },
                }}
                aria-label="Download signed note"
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Primary signer */}
        {authorEntry ? (
          <SignerRow entry={authorEntry} accentColor={accentColor} />
        ) : (
          <SignerRowFallback name={signerName} timestamp={completionTimestamp} />
        )}

        {/* Co-signatories */}
        {hasCosignatories && (
          <>
            <Divider sx={{ my: 2, borderColor: alpha(accentColor, 0.2) }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', px: 1 }}>
                Co-signatures
              </Typography>
            </Divider>
            {supervisorEntries.map((entry, index) => (
              <SignerRow key={index} entry={entry} accentColor={accentColor} />
            ))}
          </>
        )}

        {/* Awaiting co-signature placeholder */}
        {awaitingCosign && !hasCosignatories && (
          <>
            <Divider sx={{ my: 2, borderColor: alpha(accentColor, 0.2) }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', px: 1 }}>
                Co-signatures
              </Typography>
            </Divider>
            <Typography
              variant="body2"
              sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8rem' }}
            >
              A supervisor co-signature has been requested and is pending.
            </Typography>
          </>
        )}
      </Box>
    </NoteSectionContainer>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

interface SignerRowProps {
  entry: SignatureEntryResponse;
  accentColor: string;
}

const SignerRow: React.FC<SignerRowProps> = ({ entry }) => {
  const licenseDisplay =
    entry.license_number && entry.license_state
      ? `${entry.license_number} (${entry.license_state})`
      : entry.license_number || undefined;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {entry.name}
        </Typography>
        {entry.npi_number && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            NPI: {entry.npi_number}
          </Typography>
        )}
        {licenseDisplay && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            License: {licenseDisplay}
          </Typography>
        )}
        {entry.license_type && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {entry.license_type}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}
      >
        {formatDate(new Date(entry.signed_at).getTime() / 1000, 'both')}
      </Typography>
    </Box>
  );
};

interface SignerRowFallbackProps {
  name: string;
  timestamp?: number;
}

const SignerRowFallback: React.FC<SignerRowFallbackProps> = ({ name, timestamp }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
      {name}
    </Typography>
    {timestamp && (
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}
      >
        {formatDate(timestamp, 'both')}
      </Typography>
    )}
  </Box>
);
